// src/admin/AdminVakifbankTyPaymentsPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  adminVakifbankTyConfig,
  adminVakifbankTyHistory,
  adminVakifbankTySend,
  adminVakifbankTyQuery,
} from "../Api";

function h(tag, props, ...children) {
  return React.createElement(tag, props, ...children);
}

function safeDateLabel(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("tr-TR", {
      timeZone: "Europe/Istanbul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function statusBadge(status) {
  const s = String(status || "");
  let bg = "#e5e7eb";
  let color = "#374151";
  if (s === "SUCCESS") {
    bg = "#dcfce7";
    color = "#166534";
  } else if (s === "REQUIRES_QUERY" || s === "REQUIRES_MANUAL_REVIEW" || s === "UNKNOWN") {
    bg = "#fef9c3";
    color = "#854d0e";
  } else if (s === "FAILED" || s === "CANCELLED") {
    bg = "#fecaca";
    color = "#991b1b";
  }
  return h(
    "span",
    {
      style: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: bg,
        color,
      },
    },
    s || "-"
  );
}

export default function AdminVakifbankTyPaymentsPage() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);

  const [recipientName, setRecipientName] = useState("");
  const [iban, setIban] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [sourceAccount, setSourceAccount] = useState("");

  const reload = useCallback(async () => {
    setErr("");
    setLoadingHist(true);
    try {
      const [c, hist] = await Promise.all([
        adminVakifbankTyConfig(),
        adminVakifbankTyHistory(50),
      ]);
      setCfg(c || {});
      if (c?.source_account && !sourceAccount) {
        setSourceAccount(c.source_account);
      }
      setHistory(Array.isArray(hist?.items) ? hist.items : []);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoadingHist(false);
    }
  }, [sourceAccount]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSend(e) {
    e.preventDefault();
    setErr("");
    setOkMsg("");
    const amt = Number(String(amount).replace(",", "."));
    if (!recipientName.trim() || recipientName.trim().length < 2) {
      setErr("Alıcı ad soyad zorunlu.");
      return;
    }
    if (!iban.trim()) {
      setErr("IBAN zorunlu.");
      return;
    }
    if (!(amt > 0)) {
      setErr("Tutar 0'dan büyük olmalı.");
      return;
    }
    if (!window.confirm(`${amt.toFixed(2)} TL → ${recipientName.trim()}\n${iban.trim()}\n\nYandex düşümü YOK. Bankaya gönderilsin mi?`)) {
      return;
    }
    setSending(true);
    try {
      const res = await adminVakifbankTySend({
        recipient_name: recipientName.trim(),
        iban: iban.trim(),
        amount: amt,
        description: description.trim(),
        source_account: sourceAccount.trim(),
        payment_purpose_code: "9",
      });
      if (res?.ok) {
        setOkMsg(`Ödeme başarılı · ${res.payment?.kurum_islem_id || ""}`);
        setRecipientName("");
        setIban("");
        setAmount("");
        setDescription("");
      } else {
        setErr(
          res?.payment?.bank_status_description ||
            res?.detail ||
            `Durum: ${res?.payment?.status || res?.raw_status || "bilinmiyor"}`
        );
      }
      await reload();
    } catch (ex) {
      setErr(String(ex?.message || ex));
      try {
        await reload();
      } catch (_) {
        /* ignore */
      }
    } finally {
      setSending(false);
    }
  }

  async function onQuery(kid) {
    if (!kid) return;
    setErr("");
    try {
      const r = await adminVakifbankTyQuery(kid);
      const m = r?.mapped || {};
      setOkMsg(
        `Sorgu ${kid}: ${m.app_status || "-"} · ${m.bank_status_description || m.user_message || ""}`
      );
      await reload();
    } catch (ex) {
      setErr(String(ex?.message || ex));
    }
  }

  const inputStyle = { width: "100%", maxWidth: 420 };

  return h(
    "div",
    { style: { display: "grid", gap: 14 } },
    h(
      "div",
      { className: "card info-card" },
      h(
        "div",
        { className: "card-title", style: { fontWeight: 900, fontSize: 18 } },
        "Ödeme — Taksi Yönetim"
      ),
      h(
        "div",
        { className: "muted", style: { marginTop: 6, lineHeight: 1.5 } },
        "Tekil IBAN + ad soyad ile VakıfBank TÖS gönderimi. ",
        h("strong", null, "Yandex bakiye kontrolü ve düşümü yok."),
        " Kurum: ",
        cfg?.kurum_kodu_masked || "0052286"
      ),
      cfg
        ? h(
            "div",
            { className: "muted", style: { marginTop: 8, fontSize: 13 } },
            `Ortam: ${cfg.env || "—"} · Yapılandırma: ${cfg.configured ? "tamam" : "eksik"} · Hesap: ${
              cfg.source_account_masked || "formdan girilecek"
            }`,
            cfg.production_blocked
              ? h(
                  "div",
                  { style: { marginTop: 8, color: "#b91c1c", fontWeight: 600 } },
                  "Production engelli — VAKIFBANK_TY_ALLOW_PRODUCTION=1 gerekli."
                )
              : null,
            !cfg.configured
              ? h(
                  "div",
                  { style: { marginTop: 8, color: "#b91c1c", fontWeight: 600 } },
                  "VAKIFBANK_TY_* kimlik bilgileri .env içinde tanımlı olmalı; servis yeniden başlatılmalı."
                )
              : null
          )
        : null
    ),
    h(
      "div",
      { className: "card info-card" },
      h("form", { onSubmit: onSend, style: { display: "grid", gap: 12, maxWidth: 480 } }, [
        h(
          "label",
          { key: "name", style: { display: "grid", gap: 4, fontSize: 13 } },
          "Alıcı ad soyad",
          h("input", {
            className: "input",
            style: inputStyle,
            value: recipientName,
            onChange: (e) => setRecipientName(e.target.value),
            required: true,
            autoComplete: "off",
          })
        ),
        h(
          "label",
          { key: "iban", style: { display: "grid", gap: 4, fontSize: 13 } },
          "IBAN",
          h("input", {
            className: "input",
            style: inputStyle,
            value: iban,
            onChange: (e) => setIban(e.target.value.toUpperCase()),
            placeholder: "TR…",
            required: true,
            autoComplete: "off",
          })
        ),
        h(
          "label",
          { key: "amt", style: { display: "grid", gap: 4, fontSize: 13 } },
          "Tutar (TL)",
          h("input", {
            className: "input",
            style: inputStyle,
            type: "number",
            step: "0.01",
            min: "0.01",
            value: amount,
            onChange: (e) => setAmount(e.target.value),
            required: true,
          })
        ),
        h(
          "label",
          { key: "desc", style: { display: "grid", gap: 4, fontSize: 13 } },
          "Açıklama (opsiyonel)",
          h("input", {
            className: "input",
            style: inputStyle,
            value: description,
            onChange: (e) => setDescription(e.target.value),
            maxLength: 200,
          })
        ),
        h(
          "label",
          { key: "src", style: { display: "grid", gap: 4, fontSize: 13 } },
          "Kaynak hesap no (KaynakHesapNo)",
          h("input", {
            className: "input",
            style: inputStyle,
            value: sourceAccount,
            onChange: (e) => setSourceAccount(e.target.value),
            placeholder: "örn. 0015800…",
            autoComplete: "off",
          }),
          h(
            "span",
            { className: "muted", style: { fontSize: 12 } },
            ".env’de VAKIFBANK_TY_SOURCE_ACCOUNT yoksa buraya yazın."
          )
        ),
        h(
          "div",
          { key: "actions", style: { display: "flex", gap: 8, flexWrap: "wrap" } },
          h(
            "button",
            {
              type: "submit",
              className: "btn",
              disabled: sending || !cfg?.configured || cfg?.production_blocked,
            },
            sending ? "Gönderiliyor…" : "Ödeme Gönder"
          ),
          h(
            "button",
            {
              type: "button",
              className: "btn-outline",
              disabled: loadingHist,
              onClick: () => reload(),
            },
            "Yenile"
          )
        ),
      ]),
      err ? h("div", { style: { marginTop: 12, color: "#b91c1c", fontWeight: 600 } }, err) : null,
      okMsg ? h("div", { style: { marginTop: 12, color: "#166534", fontWeight: 600 } }, okMsg) : null
    ),
    h(
      "div",
      { className: "card info-card", style: { overflowX: "auto" } },
      h("div", { style: { fontWeight: 800, marginBottom: 10 } }, "Son ödemeler"),
      history.length === 0
        ? h("div", { className: "muted" }, loadingHist ? "Yükleniyor…" : "Kayıt yok.")
        : h(
            "table",
            { style: { width: "100%", borderCollapse: "collapse", fontSize: 13 } },
            h(
              "thead",
              null,
              h(
                "tr",
                { style: { textAlign: "left", borderBottom: "2px solid #e5e7eb" } },
                ["Tarih", "Alıcı", "IBAN", "Tutar", "Durum", "Banka", "İşlem"].map((col) =>
                  h("th", { key: col, style: { padding: "8px 6px" } }, col)
                )
              )
            ),
            h(
              "tbody",
              null,
              history.map((row) =>
                h(
                  "tr",
                  {
                    key: row.id || row.kurum_islem_id,
                    style: { borderBottom: "1px solid #f3f4f6" },
                  },
                  h("td", { style: { padding: "8px 6px", whiteSpace: "nowrap" } }, safeDateLabel(row.created_at)),
                  h("td", { style: { padding: "8px 6px" } }, row.recipient_name || "—"),
                  h(
                    "td",
                    { style: { padding: "8px 6px", fontFamily: "monospace", fontSize: 12 } },
                    row.iban || "—"
                  ),
                  h("td", { style: { padding: "8px 6px" } }, `${row.amount || "—"} TL`),
                  h("td", { style: { padding: "8px 6px" } }, statusBadge(row.status)),
                  h(
                    "td",
                    { style: { padding: "8px 6px", maxWidth: 220 } },
                    row.bank_status_description || row.bank_status_code || "—"
                  ),
                  h(
                    "td",
                    { style: { padding: "8px 6px" } },
                    h(
                      "button",
                      {
                        type: "button",
                        className: "btn-outline",
                        style: { fontSize: 12, padding: "2px 8px" },
                        onClick: () => onQuery(row.kurum_islem_id),
                      },
                      "Sorgula"
                    )
                  )
                )
              )
            )
          )
    )
  );
}
